import React from "react";
import { action, observable, computed, runInAction, autorun, toJS, makeObservable } from 'mobx';

import * as socialApi from '../socialApi';
import {
  FACEBOOK,
  GOOGLE,
  INSTAGRAM, INSTAGRAM_DBNAME,
  TWITTER,
  DEFAULT_FILTER, INSTAGRAM_FILTER,
  GROUPS_PER_PAGE,
  GROUP_EDIT_MODE, GROUP_ADD_MODE,
  convertProviderNiceNameToDBName, LINKEDIN, LINKEDIN_DBNAME,
  convertProviderDBNameToNiceName, convertProviderToNiceName
} from "../socialConstants";
import sessionStore from "../../../services/session/SessionStore";
import TwitterCustomSearch from "../models/TwitterCustomSearch";
import {listGroups} from "../publishing/api"
import ChatterListingGroup from "../../../modules/social/models/ChatterListingGroup";

// TODO - this store is doing too much. Would be simpler if components fetched the data they need on demand
// there's some conflicting needs for state. For example pages list wants all listings, whether or not they are managed,
// whereas the social groups page wants just managed pages
//
export class SocialPublishingStore {
  constructor() {
    makeObservable(this, {
      accountId: computed,
      socialAccounts: observable,
      publishingGroups: observable,
      listingPages: observable,
      totalPages: observable,
      hasMoreListings: observable,
      isLoading: observable,
      isLoadingListings: observable,
      isSubmitting: observable,
      phaseOneLoaded: observable,
      accountPendingCount: observable,
      errorMessage: observable,
      twitterCustomSearches: observable,
      listings: observable,
      listingsCount: observable,
      updateInfo: observable,
      filters: observable,
      publishingGroupPage: observable,
      totalPublishingGroupPages: observable,
      groupEditorPage: observable,
      totalGroupEditorPages: observable,
      listingsPage: observable,
      totalListingsPages: computed,
      groupEditMode: observable,
      selectedPublishingGroup: observable,
      selectedProviders: observable,
      selectedMediaTypes: observable,
      selectedStatuses: observable,
      selectedAuthorTypes: observable,
      publishingDestinationFilters: observable,
      loadTwitterCustomSearches: action.bound,
      setLoading: action,
      getAccountsForProvider: action,
      getAccountsCountForProvider: action,
      getListingsForProvider: action,
      getFiltersForProvider: action,
      setSelectedTabIndex: action,
      updateProviderFilters: action,
      getListingsForSelectedPublishingGroup: action,
      updateListingEnabled: action,
      getFilteredAndSortedPublishingGroups: action,
      incrementAccountPendingCount: action,
      decrementAccountPendingCount: action,
      resetAccountPendingCount: action,
      setErrorMessage: action,
      setPublishingGroupPage: action,
      setListingsPage: action,
      setGroupEditorPage: action,
      setGroupEditMode: action,
      setSelectedPublishingGroup: action,
      validateSelectedPublishingGroup: action,
      clearSelectedPublishingGroup: action,
      clearGroupEditMode: action,
      clearErrorMessage: action,
      removePublishingGroupById: action,
      submitSelectedPublishingGroup: action,
      addPublishingGroup: action,
      addTwitterCustomSearch: action,
      removeTwitterCustomSearch: action,
      backfillIdForTwitterCustomSearch: action,
      setSelectedProviders: action,
      setSelectedMediaTypes: action,
      setSelectedStatuses: action,
      setSelectedAuthorTypes: action,
      clearSelectedFilters: action,
      isPendingForAccountUpdates: computed,
      missingProviderCredentials: computed,
      allFacebookAccounts: computed,
      facebookAccounts: computed,
      instagramAccounts: computed,
      twitterAccounts: computed,
      googleAccounts: computed,
      linkedInAccounts: computed,
      instagramListingPages: computed,
      facebookListings: computed,
      instagramListings: computed,
      googleListings: computed,
      credentialsForLoggedInUser: computed
    });
  }

  /**
   * OK: so we need to merge this with the new SocialPagesStore, having 2 is unnecessary.
   * We don't want to aggressively load all the listingPages any more.
   */

  /* Observable properties. */

  get accountId() {
    return sessionStore.user.accountId;
  }

  socialAccounts = [];
  publishingGroups = [];
  listingPages = [];
  totalPages = 0;
  hasMoreListings = false;
  isLoading = false;
  isLoadingListings = false;
  isSubmitting = false;
  phaseOneLoaded = false;

  accountPendingCount = 0;
  errorMessage = null;

  twitterCustomSearches = [];

  // For the structure of the listings, see SettingStateShrunk case class in SettingState.scala.
  listings = [];
  listingsCount = 0;

  // contains meta-data about a listing last update date, keyed by a remoteId. Organized in a map.
  // Ex: "ChIJTR3AKKuD11IRGJTsSrQeiEc": {
  //       "key": "ChIJTR3AKKuD11IRGJTsSrQeiEc",
  //       "lastUpdate": "2019-11-06T08:45:39Z",
  //       "errors": []
  //     }
  updateInfo = null;

  filters = {
    selectedTabIndex: 0,
    selectedProvider: FACEBOOK,
    providers: {
      [FACEBOOK]: DEFAULT_FILTER,
      [TWITTER]: DEFAULT_FILTER,
      [INSTAGRAM]: INSTAGRAM_FILTER,
      [GOOGLE]: DEFAULT_FILTER,
      [LINKEDIN]: DEFAULT_FILTER
    }
  };

  publishingGroupPage = 1; // controlled by filter.listingGroupIndex
  totalPublishingGroupPages = 1;

  groupEditorPage = 1; // controlled by filter.listingGroupPageIndex
  totalGroupEditorPages = 1;

  listingsPage = 1; // controlled by filter.listingPageIndex
  get totalListingsPages() {
    return Math.max(Math.ceil(this.listingsCount / this.publishingDestinationFilters.limit), 1);
  }

  groupEditMode = undefined; // Supported modes are null, 'Add', and 'Edit'.
  selectedPublishingGroup = null;

  selectedProviders = [];
  selectedMediaTypes = [];
  selectedStatuses = [];
  selectedAuthorTypes = [];

  publishingDestinationFilters = {
    limit: 10,
    provider: '',
    text: ''
  };

  pageSize = 10;
  autoUpdateDisposer = null;

  init() {
    this.autoUpdateDisposer = autorun(() => {
      this.loadChatterListings().then();
      this.loadPublishingGroups().then();
    });
  }

  exterminateAutoRun() {
    if (this.autoUpdateDisposer) {
      this.autoUpdateDisposer();
      this.autoUpdateDisposer = null;
    }
  }


  /* Mobx actions. */
  loadTwitterCustomSearches = async () => {
    const response = await socialApi.getChatterCustomTwitterSearches(this.accountId);
    const {customSearches} = response;
    if (customSearches && customSearches.length) {
      this.twitterCustomSearches = customSearches;
    }
  };

  loadPublishingGroups = async () => {
    const response = await listGroups();

    runInAction(() => {
      this.publishingGroups = response.groups;
    })
  }


  loadChatterListings = async () => {
    let filtersObject = this.filters.selectedProvider ? this.filters.providers[this.filters.selectedProvider] : {};
    this.isLoadingListings = true;
    const response = await socialApi.getChatterListings(
        this.accountId,
        this.filters.selectedProvider,
        filtersObject.textSearch || "",
        (this.listingsPage - 1) * this.pageSize,   // offset
        this.pageSize,
        filtersObject.onlySourceType,
        filtersObject.onlySourceAccount,
        filtersObject.listingSortOrder === -1 ? 'DESC' : 'ASC'
    );

    runInAction(() => {
      this.isLoadingListings = false;
      this.listings = response.listings;
      this.updateInfo = response.updateInfo;
      this.listingsCount = response.listingCount;
    })
  };

  setLoading(boolVal) {
    this.isLoading = boolVal;
  }

  getAccountsForProvider(providerName) {
    const niceName = convertProviderToNiceName(providerName);
    switch (niceName) {
      case FACEBOOK: return this.facebookAccounts;
      case INSTAGRAM: return this.instagramAccounts;
      case TWITTER: return this.twitterAccounts;
      case GOOGLE: return this.googleAccounts;
      case LINKEDIN: return this.linkedInAccounts;
    }
  }

  getAccountsCountForProvider(providerName) {
    const niceName = convertProviderToNiceName(providerName);
    switch (niceName) {
      case FACEBOOK: return this.facebookAccounts.length;
      case INSTAGRAM: return this.instagramAccounts.length;
      case TWITTER: return this.twitterAccounts.length;
      case GOOGLE: return this.googleAccounts.length;
      case LINKEDIN: return this.linkedInAccounts.length;
      default: return 0;
    }
  }

  getListingsForProvider(providerName) {
    // There are no listings for Twitter.
    switch (providerName) {
      case FACEBOOK: return this.facebookListings;
      case INSTAGRAM: return this.instagramListings;
      case GOOGLE: return this.googleListings;
      default: return [];
    }
  }

  getFiltersForProvider(providerName) {
    const niceName = convertProviderToNiceName(providerName);
    switch (niceName) {
      case FACEBOOK: return this.filters.providers.Facebook;
      case TWITTER: return this.filters.providers.Twitter;
      case INSTAGRAM: return this.filters.providers.Instagram;
      case GOOGLE: return this.filters.providers.Google;
      case LINKEDIN: return this.filters.providers.LinkedIn;
      default: return undefined;
    }
  }

  setSelectedTabIndex(idx) {
    this.filters = {...this.filters, selectedTabIndex: idx};
  }

  updateProviderFilters(providerName, updatedProviderFilters) {
    const updatedProviders = {...this.filters.providers, [providerName]: updatedProviderFilters};
    this.filters = {...this.filters, providers: updatedProviders, selectedProvider: providerName};
  }

  getListingsForSelectedPublishingGroup() {
    const remoteIds = this.selectedPublishingGroup.remoteIds;
    return this.listings.filter(listing => remoteIds.includes(listing.remoteId));
  }

  updateListingEnabled(listing, isDisabled) {
    listing.isDisabled = isDisabled
  }

  getFilteredAndSortedPublishingGroups(filters, providerName) {
    const providerDBName = convertProviderNiceNameToDBName(providerName);
    const filteredPublishingGroups = this.publishingGroups.filter(group => {
      const matchText = !filters.textSearch || group.name.toLowerCase().indexOf(filters.textSearch.toLowerCase()) >= 0;
      const matchProvider = group.provider.toUpperCase() === providerDBName;
      return matchText && matchProvider;
    });
    this.totalPublishingGroupPages = Math.max(Math.ceil(filteredPublishingGroups.length / GROUPS_PER_PAGE), 1);
    const sortedPublishingGroups = filteredPublishingGroups.sort((a, b) =>
      a.name.localeCompare(b.name) * filters.listingGroupSortOrder
    );

    // Return the current page of results.
    const startIndex = (this.publishingGroupPage - 1) * GROUPS_PER_PAGE;
    const endIndex = startIndex + GROUPS_PER_PAGE;
    return sortedPublishingGroups.slice(startIndex, endIndex);
  }

  incrementAccountPendingCount() {
    this.accountPendingCount = this.accountPendingCount + 1;
  }

  decrementAccountPendingCount() {
    this.accountPendingCount = Math.max(this.accountPendingCount - 1, 0);
  }

  resetAccountPendingCount() {
    this.accountPendingCount = 0;
  }

  setErrorMessage(message) {
    this.errorMessage = message;
    this.isLoading = false;
  }

  setPublishingGroupPage(pageNumber) {
    this.publishingGroupPage = pageNumber;
  }

  setListingsPage(pageNumber) {
    this.listingsPage = pageNumber;
  }

  setGroupEditorPage(pageNumber) {
    this.groupEditorPage = pageNumber;
  }

  setGroupEditMode(mode) {
    const supportedModes = [GROUP_EDIT_MODE, GROUP_ADD_MODE];
    if (supportedModes.includes(mode)) {
      this.groupEditMode = mode;
    } else {
      console.error("Unsupported Group Edit Mode: ", mode);
    }
  }

  setSelectedPublishingGroup(group) {
    this.validateSelectedPublishingGroup(group)
    this.selectedPublishingGroup = group;
  }

  validateSelectedPublishingGroup(group) {
    this.selectedPublishingGroupExist = this.publishingGroups.find(element => element.name === group.name) !== undefined
  }

  clearSelectedPublishingGroup() {
    this.selectedPublishingGroup = null;
  }

  clearGroupEditMode() {
    this.groupEditMode = undefined;
  }

  clearErrorMessage() {
    this.errorMessage = null;
  }

  removePublishingGroupById = async (id) => {
    this.isSubmitting = true;
    await socialApi.deleteGroup(id);
    this.publishingGroups = this.publishingGroups.filter(group => {
      return group.id !== id &&
        (!group._id || group._id.$oid !== id) // for legacy group model
    });
    this.isSubmitting = false;
  };

  submitSelectedPublishingGroup = async () => {
    this.isSubmitting = true;
    const isAdding = this.groupEditMode === GROUP_ADD_MODE; // True if adding, False if editing.
    const g = this.selectedPublishingGroup;

    /* Tech debt: Social historically used the ChatterProvider values. We are now using the standard Provider names. Which means that we are
     * currently mixing names like "FACEBOOK" (Regular, desired, Provider name) and "Facebook" (legacy, undesired, ChatterProvider name).
     * In the future, we need to normalize these names, but for now, this is expected. */
    const providerChatterName = isAdding ? convertProviderDBNameToNiceName(g.provider) : g.provider;
    const chatterGroup = new ChatterListingGroup(g._id ? g._id.$oid : '', g.name, providerChatterName, g.remoteIds, g.accountId);

    isAdding
      ? await socialApi.addGroup(chatterGroup.toCreateRequest())
      : await socialApi.updateGroup(chatterGroup);

    this.isSubmitting = false;
    const providerFilter = this.getFiltersForProvider(providerChatterName);
    const updatedProviderFilter = {...providerFilter, listingPageIndex: 0, listingGroupIndex: 0, listingGroupPageIndex: 0 };
    this.updateProviderFilters(providerChatterName, updatedProviderFilter);
    this.clearGroupEditMode();
    this.clearSelectedPublishingGroup();
  };

  addPublishingGroup(group) {
    this.publishingGroups = this.publishingGroups.concat(group);
  }

  addTwitterCustomSearch(twitterSearch) {
    // Optimistic-UI: We may not have an ID yet, but we want to be as responsive as possible, so we add a
    // TwitterCustomSearch object first, then we refresh it with the ID.
    const twitterCustomSearch = new TwitterCustomSearch(undefined, this.accountId, twitterSearch);
    this.twitterCustomSearches = this.twitterCustomSearches
      .concat(twitterCustomSearch)
      .sort((a, b) => a.search.localeCompare(b.search));
  }

  removeTwitterCustomSearch(twitterSearch) {
    this.twitterCustomSearches = this.twitterCustomSearches.filter(tcs => tcs.search !== twitterSearch);
  }

  backfillIdForTwitterCustomSearch(id, search) {
    const oldTwitterCustomSearches = this.twitterCustomSearches.filter(tcs => tcs.search !== search) || [];
    const twitterCustomSearchToUpdate = this.twitterCustomSearches.filter(tcs => tcs.search === search)[0];
    const updatedTwitterCustomSearch = {...twitterCustomSearchToUpdate, _id: {$oid: id}};
    this.twitterCustomSearches = oldTwitterCustomSearches
      .concat(updatedTwitterCustomSearch)
      .sort((a, b) => a.search.localeCompare(b.search));
  }

  setSelectedProviders(providers) {
    this.selectedProviders = providers;
  }

  setSelectedMediaTypes(mediaTypes) {
    this.selectedMediaTypes = mediaTypes;
  }

  setSelectedStatuses(statuses) {
    this.selectedStatuses = statuses;
  }

  setSelectedAuthorTypes(authorTypes) {
    this.selectedAuthorTypes = authorTypes;
  }

  clearSelectedFilters() {
    this.selectedProviders = [];
    this.selectedMediaTypes = [];
    this.selectedStatuses = [];
    this.selectedAuthorTypes = [];
  }


  /* Computed Properties */

  get isPendingForAccountUpdates() {
    return this.accountPendingCount > 0;
  }

  get missingProviderCredentials() {
    const socialProviders = new Set([FACEBOOK, TWITTER, GOOGLE]);
    const existingProviders = new Set(this.socialAccounts.map(acc => acc.provider));
    return new Set([...socialProviders].filter(x => !existingProviders.has(x)));
  }

  get allFacebookAccounts() {
    return this.socialAccounts.filter(acc => acc.provider === FACEBOOK || acc.provider === INSTAGRAM);
  }

  get facebookAccounts() {
    return this.socialAccounts.filter(acc => acc.provider === FACEBOOK);
  }

  get instagramAccounts() {
    return this.socialAccounts.filter(acc => acc.provider === INSTAGRAM);
  }

  get twitterAccounts() {
    return this.socialAccounts.filter(acc => acc.provider === TWITTER);
  }

  get googleAccounts() {
    return this.socialAccounts.filter(acc => acc.provider === GOOGLE);
  }

  get linkedInAccounts() {
    return this.socialAccounts.filter(acc => acc.provider === LINKEDIN_DBNAME);
  }

  get instagramListingPages() {
    return this.listingPages.filter(listingPage => listingPage.provider === INSTAGRAM_DBNAME);
  }

  get facebookListings() {
    return this.listings.filter(listing => listing.provider === FACEBOOK);
  }

  get instagramListings() {
    return this.listings.filter(listing => listing.provider === INSTAGRAM);
  }

  get googleListings() {
    return this.listings.filter(listing => listing.provider === GOOGLE);
  }

  get credentialsForLoggedInUser() {
    const userId = sessionStore.user.userId;
    return this.socialAccounts.filter(acc => acc.userId.includes(userId)).map(acc => acc._id);
  }

  /* Helper Methods */

  normalizeTextForSearchMatching = (text) => {
    if (!text) return "";
    return text.split(/\s+/).map(sub => sub.replace(/[^\w|\s]+/g, '')).join(' ').toLowerCase();
  };

  joinDefinedStrings = (joinString) => (...args) => args.filter(x => x).join(joinString) || undefined;

  formatAddress = (address) => {
    return this.joinDefinedStrings(', ')(
      address.streetAddress,
      address.addressLocality,
      this.joinDefinedStrings(' ')(address.addressRegion, address.postalCode || address.postalCode)
    )
  };

  /** A listing is considered a chatmeter location if it has a locationId. */
  isChatmeterLocation = (chatterListing) => !!chatterListing.locationId;
  /** A listing is a user account if it has a userAccountId. */
  isUserAccount = (chatterListing) => chatterListing.userAccountId && chatterListing.userAccountId.length > 0;
  /** A listing is a store page if it has a location ID. */
  isStorePage = (chatterListing) => !!chatterListing.locationId;

  getExternalAddressForListing = (chatterListing) => {
    if (!chatterListing.address) return "";
    return this.formatAddress(chatterListing.address);
  };

  getChatmeterAddressForListing = (chatterListing) => {
    if (!this.isChatmeterLocation(chatterListing) || !chatterListing.businessAddress) return "";
    return this.formatAddress(chatterListing.businessAddress);
  };

  /** Returns all the Social Accounts that this user has access and that have access to this listing. */
  getSocialAccountsForListing = (chatterListing) => {
    if (!chatterListing.credentialIds || !chatterListing.credentialIds.length) return [];
    return this.socialAccounts.filter(account => chatterListing.credentialIds.includes(account._id));
  };

  getSearchMatcherTermsForListing = (chatterListing) => {
    const listingTerms = [
      chatterListing.name,
      chatterListing.url,
      this.getChatmeterAddressForListing(chatterListing),
      this.getExternalAddressForListing(chatterListing),
      chatterListing.locationId,
      chatterListing.remoteId
    ];

    const accounts = this.getSocialAccountsForListing(chatterListing);
    const accountRemoteNames = accounts.map(account => account.remoteAccountName);
    const accountFullNames = accounts.map(account => account.userDetails.fullName);
    const accountTerms = accountRemoteNames.concat(accountFullNames);

    return listingTerms.concat(accountTerms).map(term => this.normalizeTextForSearchMatching(term));
  };
}


export default new SocialPublishingStore();
